import type { PhreaticPredictionResponse } from '@/api/dataVault';

interface Props {
  data?: PhreaticPredictionResponse;
  isLoading: boolean;
  horizonYears: number;
}

const fmt = (v: number | null | undefined, digits = 1) =>
  v == null ? '—' : v.toLocaleString(undefined, { maximumFractionDigits: digits });

/**
 * Phreatic water-availability forecast, computed on load via the V6 assessment
 * service. Shown when the Phreatic product is the active toggle. Self-contained
 * so it doesn't entangle with the chart-driven tab rendering.
 */
export function PhreaticPredictionsPanel({ data, isLoading, horizonYears }: Props) {
  const horizonLabel = horizonYears === 0 ? 'Now' : `+${horizonYears}yr`;
  if (isLoading) {
    return (
      <div className="text-xs opacity-60 px-1 py-3">
        Running phreatic inference…
      </div>
    );
  }

  const items = data?.items ?? [];
  if (items.length === 0) {
    return (
      <div className="text-xs opacity-60 px-1 py-3">
        Select one or more sites to see phreatic predictions.
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-400/30 overflow-hidden mb-3">
      <div className="px-3 py-2 text-[11px] uppercase tracking-wide opacity-60 border-b border-gray-400/20">
        Phreatic Predictions {data?.truncated ? '(showing first 8 sites)' : ''}
      </div>
      <table className="w-full text-xs">
        <thead className="opacity-60">
          <tr className="text-left">
            <th className="px-3 py-2">Site</th>
            <th className="px-3 py-2">Aquifer</th>
            <th className="px-3 py-2 text-right">Years Remaining</th>
            <th className="px-3 py-2 text-right">Decline (ft/yr)</th>
            <th className="px-3 py-2 text-right">Avg Sat Thickness (ft)</th>
            <th className="px-3 py-2 text-right">AF Available</th>
            <th className="px-3 py-2 text-right">AF @ {horizonLabel}</th>
            <th className="px-3 py-2 text-right">Wells</th>
          </tr>
        </thead>
        <tbody>
          {items.map((r) => {
            const afAtHorizon = r.forecast?.find((f) => f.year === horizonYears)?.af_remaining ?? null;
            return (
              <tr key={r.site_id} className="border-t border-gray-400/15">
                <td className="px-3 py-2">{r.site_name}</td>
                <td className="px-3 py-2">{r.aquifer ?? '—'}</td>
                {r.error ? (
                  <td className="px-3 py-2 opacity-60 italic" colSpan={6}>
                    {r.error}
                  </td>
                ) : (
                  <>
                    <td className="px-3 py-2 text-right">{fmt(r.years_remaining, 0)}</td>
                    <td className="px-3 py-2 text-right">{fmt(r.decline_ft_yr, 2)}</td>
                    <td className="px-3 py-2 text-right">{fmt(r.avg_sat_thickness_ft, 0)}</td>
                    <td className="px-3 py-2 text-right">{fmt(r.af_available, 0)}</td>
                    <td className="px-3 py-2 text-right">{fmt(afAtHorizon, 0)}</td>
                    <td className="px-3 py-2 text-right">{fmt(r.wells_analyzed, 0)}</td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
